import { Query } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { clamp } from '@mantine/hooks';
import {
	attach,
	combine,
	createEvent,
	createStore,
	EventCallable,
	restore,
	sample,
	Store,
	Unit,
} from 'effector';
import { createGate, Gate } from 'effector-react';
import { debounce, equals, not, or, spread } from 'patronum';
import { Paginated } from 'shared/api';
import { Sourced } from './create-sourced';
import { SourcedMap } from './create-sourced-map';
import { createState, State } from './create-state';
import { createStateMap, StateMap } from './create-state-map';

export const SORT_DIRECTIONS = {
	ASC: 'ASC',
	DESC: 'DESC',
} as const satisfies Record<string, string>;

export type SortDirection = (typeof SORT_DIRECTIONS)[keyof typeof SORT_DIRECTIONS];

export type Row = {
	id: number | string;
};

export type RegistryQuery<
	TRow extends Row = Row,
	TFilters extends Record<string, unknown> = Record<string, unknown>,
	TError = unknown,
> = Query<RegistryParams<TFilters>, Paginated<TRow>, TError, null>;

export type RegistryModel<
	TRow extends Row = Row,
	TFilters extends Record<string, unknown> = Record<string, unknown>,
	TError = unknown,
> = {
	query: RegistryQuery<TRow, TFilters, TError>;
	$rows: Store<TRow[]>;
	$initialized: Store<boolean>;
	$loading: Store<boolean>;
	$total: Store<number>;
	page: State<number>;
	limit: State<number>;
	sortField: State<string | null>;
	sortDirection: State<SortDirection>;
	filters: StateMap<TFilters>;
	selection: State<(number | string)[]>;
	$disabled: Store<boolean>;
	$allSelected: Store<boolean>;
	$totalPages: Store<number>;
	$rowsEmpty: Store<boolean>;
	$filters: Store<TFilters>;
	$filtersDirty: Store<boolean>;
	$lastFilters: Store<TFilters | null>;
	$lastFiltersDirty: Store<boolean>;
	$empty: Store<boolean>;
	$totalSelection: Store<number>;
	$emptySelection: Store<boolean>;
	$paginationStartDisabled: Store<boolean>;
	$paginationEndDisabled: Store<boolean>;
	$paginationPreviousDisabled: Store<boolean>;
	$paginationNextDisabled: Store<boolean>;
	$paginationHidden: Store<boolean>;
	tableRef: State<HTMLDivElement | null>;
	scrollToTop: EventCallable<void>;
	sorterClicked: EventCallable<string>;
	rowSelected: EventCallable<number | string>;
	rowDeselected: EventCallable<number | string>;
	rowToggled: EventCallable<number | string>;
	allRowsSelected: EventCallable<void>;
	allRowsDeselected: EventCallable<void>;
	allRowsToggled: EventCallable<void>;
	paginationStartClicked: EventCallable<void>;
	paginationEndClicked: EventCallable<void>;
	paginationPreviousClicked: EventCallable<void>;
	paginationNextClicked: EventCallable<void>;
	clearFilters: EventCallable<void>;
	fetch: EventCallable<void>;
	gate: Gate<unknown>;
};

export type RegistryParams<TFilters extends Record<string, unknown>> = {
	page: number;
	limit: number;
	sortDirection: SortDirection;
	sortField: string | null;
	filters: TFilters;
};

export type RegistryModelParams<
	TRow extends Row = Row,
	TFilters extends Record<string, unknown> = Record<string, unknown>,
	TError = unknown,
> = {
	query: RegistryQuery<TRow, TFilters, TError>;
	filters: SourcedMap<TFilters>;
	page?: Sourced<number>;
	limit?: Sourced<number>;
	sortField?: Sourced<string | null>;
	sortDirection?: Sourced<SortDirection>;
	selection?: Sourced<(number | string)[]>;
	disabled?: Store<boolean>;
	onUpdate?: Unit<unknown>[];
	onDelete?: Unit<unknown>[];
	onReset?: Unit<unknown>[];
	resetOnClose?: boolean;
};

export type RegistryCallbacks = Pick<
	RegistryModelParams,
	'onUpdate' | 'onDelete' | 'onReset' | 'disabled' | 'resetOnClose'
>;

export function createRegistryModel<
	TRow extends Row,
	TFilters extends Record<string, unknown>,
	TError,
>({
	query,
	...params
}: RegistryModelParams<TRow, TFilters, TError>): RegistryModel<TRow, TFilters, TError> {
	const $rows = query.$data.map((data) => data?.rows ?? []);

	const $initialized = or(query.$data, query.$error);

	const $loading = query.$pending;

	const $total = query.$data.map((data) => data?.total ?? 0);

	const gate = createGate();

	const $disabled = combine(
		{ disabled: params.disabled ?? createStore(false), gateStatus: gate.status },
		({ disabled, gateStatus }) => disabled || !gateStatus,
	);

	const page = createState(params.page ?? 0);

	const limit = createState(params.limit ?? 10);

	const sortField = createState(params.sortField ?? null);

	const sortDirection = createState(params.sortDirection ?? SORT_DIRECTIONS.ASC);

	const filters = createStateMap(params.filters);

	const selection = createState(params.selection ?? []);

	const $totalPages = combine([limit.$value, $total], ([lmt, ttl]) => Math.ceil(ttl / lmt));

	const $rowsEmpty = $rows.map((rws) => rws === null || rws.length === 0);

	const $allSelected = combine(
		[$rowsEmpty, $rows, selection.$value],
		([empty, rws, sel]) => !empty && (rws?.every((row) => sel.includes(row.id)) ?? false),
	);

	const $filters = combine(
		Object.keys(filters).reduce(
			(acc, key) => ({
				...acc,
				[key]: filters[key].$value,
			}),
			{} as SourcedMap<TFilters>,
		),
	) as Store<TFilters>;

	const $filtersDirty = $filters.map((fts) =>
		Object.keys(fts).some((key) => fts[key] !== filters[key].$value.defaultState),
	);

	const $lastFilters = restore(
		query.finished.success.map((pld) => pld.params.filters),
		null,
	);

	const $lastFiltersDirty = $lastFilters.map(
		(fts) =>
			fts !== null && Object.keys(fts).some((key) => fts[key] !== filters[key].$value.defaultState),
	);

	const $empty = combine([$lastFiltersDirty, $rowsEmpty], ([dirty, empty]) =>
		Object.keys(params.filters).length === 0 ? empty : !dirty && empty,
	);

	const $params = combine({
		page: page.$value,
		limit: limit.$value,
		sortField: sortField.$value,
		sortDirection: sortDirection.$value,
		filters: $filters,
	});

	const $paramsWithoutPage = combine({
		limit: limit.$value,
		sortField: sortField.$value,
		sortDirection: sortDirection.$value,
		filters: $filters,
	});

	const $totalSelection = selection.$value.map((sel) => sel.length);

	const $emptySelection = selection.$value.map((sel) => sel.length === 0);

	const $paginationStartDisabled = equals(page.$value, 0);

	const $paginationEndDisabled = combine(
		[page.$value, $totalPages],
		([pg, totalPg]) => pg >= totalPg - 1,
	);

	const $paginationPreviousDisabled = $paginationStartDisabled;

	const $paginationNextDisabled = $paginationEndDisabled;

	const $paginationHidden = $totalPages.map((totalPg) => totalPg <= 1);

	const tableRef = createState<HTMLDivElement | null>(null);

	const scrollToTop = attach({
		source: tableRef.$value,
		effect: (table) => table?.scrollTo(0, 0),
	}).prepend<void>(() => {});

	const sorterClicked = createEvent<string>();

	const rowSelected = createEvent<number | string>();

	const rowDeselected = createEvent<number | string>();

	const rowToggled = createEvent<number | string>();

	const allRowsSelected = createEvent();

	const allRowsDeselected = createEvent();

	const allRowsToggled = createEvent();

	const paginationStartClicked = createEvent();

	const paginationEndClicked = createEvent();

	const paginationPreviousClicked = createEvent();

	const paginationNextClicked = createEvent();

	const clearFilters = createEvent();

	const reset = createEvent();

	const fetch = createEvent();

	const filtersReinit = (Object.values(filters) as State<unknown>[]).map((f) => f.$value.reinit);

	sample({
		clock: sorterClicked,
		source: [sortField.$value, sortDirection.$value],
		fn: ([field, direction], clickedField) => ({
			field: clickedField,
			direction:
				clickedField === field && direction === SORT_DIRECTIONS.ASC
					? SORT_DIRECTIONS.DESC
					: SORT_DIRECTIONS.ASC,
		}),
		target: spread({
			field: sortField.$value,
			direction: sortDirection.$value,
		}),
	});

	sample({
		clock: rowSelected,
		source: selection.$value,
		fn: (sel, id) => [...new Set([...sel, id])],
		target: selection.$value,
	});

	sample({
		clock: rowDeselected,
		source: selection.$value,
		fn: (sel, id) => sel.filter((selId) => selId !== id),
		target: selection.$value,
	});

	sample({
		clock: rowToggled,
		source: selection.$value,
		fn: (sel, id) =>
			sel.includes(id) ? sel.filter((selId) => selId !== id) : [...new Set([...sel, id])],
		target: selection.$value,
	});

	sample({
		clock: allRowsSelected,
		source: {
			rws: $rows,
			selected: selection.$value,
		},
		fn: ({ rws, selected }) => [...new Set([...selected, ...(rws?.map((row) => row.id) ?? [])])],
		target: selection.$value,
	});

	sample({
		clock: allRowsDeselected,
		source: {
			rws: $rows,
			selected: selection.$value,
		},
		fn: ({ rws, selected }) => selected.filter((id) => !rws?.find((row) => row.id === id)),
		target: selection.$value,
	});

	sample({
		clock: allRowsToggled,
		source: [$rows, selection.$value] as const,
		fn: ([rws, sel]) => rws?.map((row) => row.id).filter((id) => !sel.includes(id)) ?? [],
		target: selection.$value,
	});

	sample({
		clock: $totalPages,
		source: page.$value,
		fn: (pg, totalPgs) => clamp(pg, 0, Math.max(0, totalPgs - 1)),
		target: page.$value,
	});

	sample({
		clock: [$paramsWithoutPage, paginationStartClicked],
		fn: () => 0,
		target: page.$value,
	});

	sample({
		clock: paginationEndClicked,
		source: $totalPages,
		fn: (totalPg) => Math.max(totalPg - 1, 0),
		target: page.$value,
	});

	sample({
		clock: paginationPreviousClicked,
		source: page.$value,
		fn: (pg) => Math.max(pg - 1, 0),
		target: page.$value,
	});

	sample({
		clock: paginationNextClicked,
		source: [page.$value, $totalPages] as const,
		fn: ([pg, totalPgs]) => clamp(pg + 1, 0, Math.max(0, totalPgs - 1)),
		target: page.$value,
	});

	chain($rows, scrollToTop);

	sample({
		clock: clearFilters,
		target: filtersReinit,
	});

	sample({
		clock: [...(params?.onReset ?? []), reset],
		target: [query.reset, page.$value.reinit, limit.$value.reinit, ...filtersReinit],
	});

	chain(gate.open, fetch);

	sample({
		clock: fetch,
		source: $params,
		filter: not($disabled),
		target: query.start,
	});

	sample({
		clock: debounce($params, 300),
		filter: not($disabled),
		target: query.start,
	});

	sample({
		clock: params?.onUpdate ?? [],
		filter: not($disabled),
		target: fetch,
	});

	sample({
		clock: params?.onDelete || [],
		source: { rowsLength: $rows.map((rows) => rows.length), dis: $disabled },
		filter: ({ rowsLength, dis }) => rowsLength > 1 && !dis,
		target: fetch,
	});

	sample({
		clock: params?.onDelete ?? [],
		source: {
			p: page.$value,
			rowsLength: $rows.map((rows) => rows.length),
			dis: $disabled,
		},
		filter: ({ rowsLength, dis }) => rowsLength <= 1 && !dis,
		fn: ({ p }) => Math.max(0, p - 1),
		target: page.setValue,
	});

	sample({
		clock: params?.onDelete ?? [],
		source: {
			p: page.$value,
			rowsLength: $rows.map((rows) => rows.length),
			dis: $disabled,
		},
		filter: ({ rowsLength, p, dis }) => rowsLength <= 1 && p === 0 && !dis,
		target: fetch,
	});

	sample({
		clock: gate.close,
		filter: () => params.resetOnClose ?? true,
		target: query.reset,
	});

	return {
		query,
		$rows,
		$initialized,
		$loading,
		$total,
		page,
		limit,
		sortField,
		sortDirection,
		filters,
		selection,
		$disabled,
		$allSelected,
		$totalPages,
		$rowsEmpty,
		$filters,
		$filtersDirty,
		$lastFilters,
		$lastFiltersDirty,
		$empty,
		$totalSelection,
		$emptySelection,
		$paginationStartDisabled,
		$paginationEndDisabled,
		$paginationPreviousDisabled,
		$paginationNextDisabled,
		$paginationHidden,
		tableRef,
		scrollToTop,
		sorterClicked,
		rowSelected,
		rowDeselected,
		rowToggled,
		allRowsSelected,
		allRowsDeselected,
		allRowsToggled,
		paginationStartClicked,
		paginationEndClicked,
		paginationPreviousClicked,
		paginationNextClicked,
		clearFilters,
		fetch,
		gate,
	};
}
